import _ from 'underscore';
import $ from 'jquery';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useState, useCallback, useMemo } from 'react';
import {
  Panel,
  PanelType,
  PrimaryButton,
  DefaultButton,
} from '@fluentui/react';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getBmcMtEndpoint, hasError } from '@bingads-webui-campaign/cpg-product-feed-grid';
import { Loading } from '@bingads-webui-campaign-react/loading';
import { AlertHelper } from '@bingads-webui-campaign/alert-helper';
import { HelpIcon } from '@bingads-webui-campaign-react/help';

import { BrandSelector } from './brand-selector';
import { getAddBrandPanelV2Styles } from '../../style';

const text = {
  add: _TL_('Add'),
  cancel: _TL_('Cancel'),
};

const overlayProps = {
  styles: {
    root: {
      top: 60,
    },
  },
};

const AddBrandPanelUnstyled = (props) => {
  const {
    isOpen,
    classes,
    onClose,
    i18n,
    scope,
    brandGrid,
    activity,
    logScope,
    setRefreshDataTile,
    perfMarker,
    brandsList,
  } = props;

  const { aid, cid, uid } = scope.values;
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const AlertCenter = useMemo(() => new AlertHelper({ i18n }), [i18n]);

  const addBrands = useCallback(() => {
    setLoading(true);
    const childPerfMarker = perfMarker.createChild('Simplified_BatchAddBrand');
    childPerfMarker.willInit();
    const brandsData = _.map(
      selectedBrands,
      (brand, index) => `BrandsBatch[${index}][BrandName]=${encodeURIComponent(brand.BrandName)}&BrandsBatch[${index}][BrandUrl]=${encodeURIComponent(brand.BrandUrl)}`
    ).join('&');

    activity.trace(`${logScope}|AddBrandPanel|save brand request data: ${JSON.stringify(selectedBrands)}`);

    $.ajax({
      url: `${getBmcMtEndpoint()}/api/v1/manufacturers/${cid}/brands/batch?cid=${cid}&aid=${aid}&uid=${uid}`,
      type: 'POST',
      data: brandsData,
      xhrFields: {
        withCredentials: true,
      },
      success: (res) => {
        childPerfMarker.done();
        if (!hasError(res)) {
          const searchAddedBrand = _.filter(selectedBrands, brand => brand.MmcBrandId);
          activity.trace(`${logScope}|AddBrandPanel|save brand Succeed|SearchAddedBrandLength: ${searchAddedBrand.length}|ManuallyAddedBrandLength: ${selectedBrands.length - searchAddedBrand.length}`);
          AlertCenter.dismissAlert();
          setSelectedBrands([]);
          onClose(true);
          if (brandGrid) {
            brandGrid.fetchGridData = true;
            brandGrid.grid.gridView.refresh(true);
            setRefreshDataTile(true);
          }
        } else {
          activity.error(`${logScope}|AddBrandPanel|save brand error: ${res.Messages}`);
          AlertCenter.showAlert(res.Messages);
        }
        setLoading(false);
      },
      error: (err) => {
        childPerfMarker.done();
        setLoading(false);
        activity.error(`${logScope}|AddBrandPanel|save brand request error: ${JSON.stringify(err)}`);
        AlertCenter.showAlert(_.get(err, ['responseJSON', 'Message']));
      },
    });
  }, [selectedBrands, aid, cid, uid, onClose, brandGrid, AlertCenter, activity, logScope, setRefreshDataTile, perfMarker]);

  const onBrandSelectedChange = useCallback((brands) => {
    setSelectedBrands(brands);
  }, [setSelectedBrands]);

  const cancelAddBrands = useCallback(() => {
    setSelectedBrands([]);
    onClose();
  }, [onClose]);

  return (
    <Panel
      className={classNames(classes.panel)}
      type={PanelType.custom}
      isOpen={isOpen}
      hasCloseButton={false}
      customWidth="600px"
      overlayProps={overlayProps}
    >
      {loading && <Loading isVisible />}
      <h3 className={classNames(classes.title)}>
        {i18n.getString(_TL_('Add your brands'))}
        <HelpIcon topic="pop_BA_a" />
      </h3>
      <div className={classNames(['alert', 'alert-info', classes.alertInfo])}>
        <div className="alert-image" />
        <div className="alert-info-inline">
          {i18n.getString(_TL_('All brands added here is applied at a manager account level and can have implications on all your campaigns.'))}
        </div>
      </div>
      <div className={classes.content}>
        <BrandSelector
          i18n={i18n}
          classes={classes}
          onChange={onBrandSelectedChange}
          scope={scope}
          activity={activity}
          logScope={logScope}
          perfMarker={perfMarker}
          brandsList={brandsList}
        />
      </div>
      <div className={classes.bottomBar}>
        <PrimaryButton
          className={classNames(classes.saveButton, 'save-brand-button')}
          onClick={addBrands}
          disabled={_.size(selectedBrands) === 0}
          aria-label={i18n.getString(text.add)}
        >
          {i18n.getString(text.add)}
        </PrimaryButton>
        <DefaultButton
          className="cancel-brand-button"
          onClick={cancelAddBrands}
          aria-label={i18n.getString(text.cancel)}
        >
          {i18n.getString(text.cancel)}
        </DefaultButton>
      </div>
    </Panel>
  );
};

AddBrandPanelUnstyled.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  classes: PropTypes.shape({
    panel: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    searchBrandsContainer: PropTypes.string.isRequired,
    brands: PropTypes.string.isRequired,
    results: PropTypes.string.isRequired,
    searchBoxContainer: PropTypes.string.isRequired,
    searchButton: PropTypes.string.isRequired,
    searchBox: PropTypes.string.isRequired,
    count: PropTypes.string.isRequired,
    selectedBrandName: PropTypes.string.isRequired,
    bottomBar: PropTypes.string.isRequired,
    saveButton: PropTypes.string.isRequired,
    subtitle: PropTypes.string.isRequired,
    alertInfo: PropTypes.string.isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({
      aid: PropTypes.string.isRequired,
      cid: PropTypes.string.isRequired,
      uid: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
  }).isRequired,
  brandGrid: PropTypes.shape({
    fetchGridData: PropTypes.bool.isRequired,
    grid: PropTypes.shape({
      gridView: PropTypes.shape({
        refresh: PropTypes.func.isRequired,
      }).isRequired,
    }).isRequired,
  }),
  activity: PropTypes.shape({
    trace: PropTypes.func.isRequired,
    error: PropTypes.func.isRequired,
  }).isRequired,
  logScope: PropTypes.string.isRequired,
  setRefreshDataTile: PropTypes.func.isRequired,
  perfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  brandsList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

AddBrandPanelUnstyled.defaultProps = {
  brandGrid: undefined,
};

export const AddBrandPanelV2 = withDefaultStyles(AddBrandPanelUnstyled, getAddBrandPanelV2Styles);
